import React, { useCallback, useEffect, useMemo } from "react";
import { Navbar } from "../../components/Navbar";
import { ProgressTable } from "./ProgressTable";
import { useMoodleV2 } from "../../contexts/moodle";
import { ProgressRowItem } from "./ProgressRowItem";
import { ProgressRowItemDetail } from "./ProgressRowItemDetail";
import { useAuth } from "../../contexts/AuthContext";
import { requestAutoLogin } from "../../services/moodle/userauth";
import { baseURL } from "../../services/apirest";
import { useTheme } from "../../contexts/ThemeContext";
import { readableColor } from "polished";
import { useConfig } from "../../contexts/config";
import { ProgressKpiCard } from "./ProgressKpiCard";
import { CircularProgress, Skeleton } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  // getCoreCourseContent,
  getCourseCertificate,
  getGradeReportbyCourse,
  getUserProgressSummary,
} from "../../services/moodle/courses";
import { extractCourseFinalGrade } from "./courseFinalGrade";
// import { extractCourseCertificate } from "./courseCertificates";
import { mapWithConcurrency } from "./mapWithConcurrency";
import { MultiLangText } from "../../utils/multilang";
import {
  buildFallbackProgressRow,
  deriveProgressKpis,
  findRowsNeedingFallback,
  mergeCoursesWithProgressSummary,
  sortProgressRows,
} from "./progressData";

const EMPTY_CERTIFICATE = { available: false, cmid: null, url: null, label: "-" };

const HISTORY_LABELS = {
  title: "Progreso",
  kpiTitle: "Resumen de progreso",
  totalCourses: "Cursos totales",
  completedCourses: "Completados",
  inProgressCourses: "En progreso",
  course: "Curso",
  finalGrade: "Nota final",
  progress: "Progreso",
  start: "Inicio",
  end: "Término",
  certificate: "Certificado",
  survey: "Encuesta",
  download: "Descargar",
  downloading: "Generando...",
  downloaded: "¡Descargado!",
  pending: "Pendiente",
  unavailable: "No disponible",
  noEndDate: "Sin fecha de término",
  back: "Volver",
  noEvaluations: "Este curso se aprueba por visualización de contenidos y no registra calificaciones numéricas.",
  certificateUnavailable: "Este curso no cuenta con certificado emitido",
  certificatePending: "Disponible al completar el 100% del curso",
  inProgress: "En progreso",
  loading: "Cargando progreso...",
  loadingDetail: "Cargando notas...",
};

const getHistoryLabel = (history, key, fallback) => history?.[key] || fallback;

const normalizeSummaryRows = (summary) => {
  if (Array.isArray(summary)) return summary;
  if (Array.isArray(summary?.courses)) return summary.courses;
  if (Array.isArray(summary?.progress)) return summary.progress;
  if (Array.isArray(summary?.data)) return summary.data;
  return [];
};

const withDownloadOwn = (url) => {
  if (!url) return null;
  return url.includes("downloadown=") ? url : `${url}${url.includes("?") ? "&" : "?"}downloadown=1`;
};

const extractDownloadUrl = (payload) => {
  if (!payload) return null;
  if (typeof payload === "string") return payload;
  return payload.url || payload.downloadurl || payload.download_url || payload.fileurl || payload.file_url || null;
};

const downloadBase64Pdf = (payload, courseName) => {
  if (!payload?.filecontent) return false;

  const byteCharacters = atob(payload.filecontent);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const blobUrl = window.URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = blobUrl;
  downloadLink.download = payload.filename || `Certificado_${courseName || "curso"}.pdf`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(blobUrl);
  return true;
};

const Progress = () => {
  const theme = useTheme();
  const moodle = useMoodleV2();
  const { config } = useConfig();
  const { lang, user, dictionary } = useAuth();
  const [progressRows, setProgressRows] = React.useState([]);
  const [loadingRows, setLoadingRows] = React.useState(true);
  const [sessionKey, setSessionKey] = React.useState(null);
  const [downloadStatusByCourseId, setDownloadStatusByCourseId] = React.useState({});
  const [sortConfig, setSortConfig] = React.useState({ key: "courseName", direction: "asc" });
  const [selectedCourseId, setSelectedCourseId] = React.useState(null);
  const [selectedCourseName, setSelectedCourseName] = React.useState("");
  const [detailData, setDetailData] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const isDownloadingCertificate = useMemo(
    () => Object.values(downloadStatusByCourseId).some((status) => status === "loading"),
    [downloadStatusByCourseId]
  );

  const history = dictionary?.History || lang?.History;
  const labels = useMemo(() => ({
    title: getHistoryLabel(history, "first_title", HISTORY_LABELS.title),
    kpiTitle: getHistoryLabel(history, "second_title", HISTORY_LABELS.kpiTitle),
    totalCourses: getHistoryLabel(history, "kpi_total_courses", HISTORY_LABELS.totalCourses),
    completedCourses: getHistoryLabel(history, "kpi_completed_courses", getHistoryLabel(history, "complete", HISTORY_LABELS.completedCourses)),
    inProgressCourses: getHistoryLabel(history, "kpi_courses_in_progress", HISTORY_LABELS.inProgressCourses),
    course: getHistoryLabel(history, "first_column", HISTORY_LABELS.course),
    finalGrade: getHistoryLabel(history, "second_column", HISTORY_LABELS.finalGrade),
    progress: getHistoryLabel(history, "progress_column", HISTORY_LABELS.progress),
    start: getHistoryLabel(history, "third_column", HISTORY_LABELS.start),
    end: getHistoryLabel(history, "fourth_column", HISTORY_LABELS.end),
    certificate: getHistoryLabel(history, "fifth_column", HISTORY_LABELS.certificate),
    survey: getHistoryLabel(history, "sixth_column", HISTORY_LABELS.survey),
    download: getHistoryLabel(history, "fifth_column_answer", HISTORY_LABELS.download),
    downloading: getHistoryLabel(history, "certificate_downloading", HISTORY_LABELS.downloading),
    downloaded: getHistoryLabel(history, "certificate_downloaded", HISTORY_LABELS.downloaded),
    pending: getHistoryLabel(history, "pending", HISTORY_LABELS.pending),
    unavailable: getHistoryLabel(history, "unavailable", HISTORY_LABELS.unavailable),
    noEndDate: getHistoryLabel(history, "fourth_column_answer", HISTORY_LABELS.noEndDate),
    back: getHistoryLabel(history, "back", HISTORY_LABELS.back),
    noEvaluations: getHistoryLabel(history, "no_evaluations", HISTORY_LABELS.noEvaluations),
    certificateUnavailable: getHistoryLabel(history, "certificate_unavailable", HISTORY_LABELS.certificateUnavailable),
    certificatePending: getHistoryLabel(history, "certificate_pending", HISTORY_LABELS.certificatePending),
    inProgress: getHistoryLabel(history, "in_progress", HISTORY_LABELS.inProgress),
    loading: getHistoryLabel(history, "loading", HISTORY_LABELS.loading),
    loadingDetail: getHistoryLabel(history, "loading_detail", HISTORY_LABELS.loadingDetail),
  }), [history]);

  const includeEncuesta = Boolean(config?.opciones?.includes("encuesta"));
  const includeFeedback = Boolean(config?.opciones?.includes("feedback"));

  const tableTitles = useMemo(() => [
    { label: labels.course, key: "courseName", sortable: true },
    { label: labels.progress, key: "progress", sortable: true, width: "110px", align: "center" },
    { label: labels.finalGrade, key: "finalGrade", sortable: true, width: "130px", align: "center" },
    { label: labels.start, key: "startdate", sortable: true, width: "120px" },
    { label: labels.end, key: "enddate", sortable: true, width: "140px" },
    // { label: labels.certificate, key: "certificate", sortable: true, width: "140px" },
    ...(includeEncuesta ? [{ label: labels.survey, sortable: false, width: "100px" }] : []),
  ], [includeEncuesta, labels]);

  const detailTitles = useMemo(() => [
    { label: getHistoryLabel(history, "inner_first_column", "Evaluaciones") },
    { label: getHistoryLabel(history, "inner_second_column", "Calificación"), width: "120px" },
    ...(includeFeedback ? [
      { label: getHistoryLabel(history, "inner_fourth_column", "Retroalimentación"), width: "180px" },
      { label: getHistoryLabel(history, "inner_fifth_column", "Retroalimentación global"), width: "180px" },
    ] : []),
  ], [history, includeFeedback]);

  const sortedRows = useMemo(() => sortProgressRows(progressRows, sortConfig.key, sortConfig.direction), [progressRows, sortConfig]);
  const kpis = useMemo(() => deriveProgressKpis(progressRows), [progressRows]);
  const renderLoadingOverlay = useCallback((label) => (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-[8px] backdrop-blur-[1px]"
      style={{
        backgroundColor: theme.mode === "dark" ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.72)",
      }}
      role="status"
      aria-live="polite"
    >
      <CircularProgress size={34} thickness={4.5} sx={{ color: theme.colors.textColorDefault }} />
      <span
        className="font-lato text-[14px] font-semibold"
        style={{ color: theme.colors.textColorDefault }}
      >
        {label}
      </span>
    </div>
  ), [theme.colors.textColorDefault, theme.mode]);

  useEffect(() => {
    if (!isDownloadingCertificate) return undefined;

    const cursorStyle = document.createElement("style");
    cursorStyle.setAttribute("data-progress-certificate-cursor", "true");
    cursorStyle.textContent = "html, body, html *, body * { cursor: wait !important; }";
    document.head.appendChild(cursorStyle);

    return () => {
      cursorStyle.remove();
    };
  }, [isDownloadingCertificate]);

  useEffect(() => {
    if (!sessionKey && user?.username && user?.token) {
      requestAutoLogin({ username: user.username, tipddytoken: user.token, baseUrl: baseURL }).then((res) => {
        setSessionKey(res.key);
      });
    }
  }, [sessionKey, user?.token, user?.username]);

  useEffect(() => {
    !moodle.courses && moodle.getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveFallbackRow = useCallback(async (row) => {
    if (!user?.token || !user?.id) {
      return buildFallbackProgressRow(row.course, {}, row.certificate || EMPTY_CERTIFICATE);
    }

    const needsGradeFallback = row.finalGrade === "-" || row.progressPercent === null || row.progressPercent === undefined;
    // const needsCertificateFallback = !row.certificate?.available;
    const [gradeResult] = await Promise.allSettled([
      needsGradeFallback
        ? getGradeReportbyCourse({ token: user.token, id: user.id, course_id: row.courseId })
        : Promise.resolve(null),
      // needsCertificateFallback
      //   ? getCoreCourseContent({ token: user.token, course_id: row.courseId })
      //   : Promise.resolve(null),
    ]);

    const fallbackGrade = gradeResult.status === "fulfilled" && gradeResult.value
      ? extractCourseFinalGrade(gradeResult.value)
      : {};
    const grade = {
      finalGrade: row.finalGrade && row.finalGrade !== "-" ? row.finalGrade : fallbackGrade.finalGrade,
      progressPercent: row.progressPercent !== null && row.progressPercent !== undefined ? row.progressPercent : fallbackGrade.progressPercent,
    };

    return buildFallbackProgressRow(row.course, grade, row.certificate || EMPTY_CERTIFICATE);
  }, [user?.id, user?.token]);

  useEffect(() => {
    let cancelled = false;

    const resolveProgressRows = async () => {
      const courses = Array.isArray(moodle.courses) ? moodle.courses : [];
      if (!courses.length) {
        setProgressRows([]);
        setLoadingRows(Boolean(!moodle.courses));
        return;
      }

      setLoadingRows(true);
      let summaryRows = [];
      if (user?.token && user?.id) {
        try {
          summaryRows = normalizeSummaryRows(await getUserProgressSummary({ token: user.token, userid: user.id }));
        } catch (error) {
          summaryRows = [];
        }
      }

      const mergedRows = mergeCoursesWithProgressSummary(courses, summaryRows);
      const rowsNeedingFallback = findRowsNeedingFallback(mergedRows);
      const fallbackRows = await mapWithConcurrency(rowsNeedingFallback, resolveFallbackRow, 3);
      const fallbackByCourseId = new Map(fallbackRows.map((row) => [Number(row.courseId), row]));
      const resolvedRows = mergedRows.map((row) => fallbackByCourseId.get(Number(row.courseId)) || row);

      if (!cancelled) {
        setProgressRows(resolvedRows);
        setLoadingRows(false);
      }
    };

    resolveProgressRows();

    return () => {
      cancelled = true;
    };
  }, [moodle.courses, resolveFallbackRow, user?.id, user?.token]);

  const handleDownloadCertificate = useCallback(async (courseId) => {
    const row = progressRows.find((progressRow) => Number(progressRow.courseId) === Number(courseId));
    const certificate = row?.certificate;
    if (!certificate?.available && !certificate?.url && !certificate?.cmid) return;

    setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "loading" }));
    try {
      let certificatePayload = null;
      let downloadUrl = null;
      if (user?.token && certificate?.cmid) {
        try {
          certificatePayload = await getCourseCertificate({
            token: user.token,
            cmid: certificate.cmid,
            userid: user?.id || 0,
            baseUrl: config?.baseURL || baseURL,
          });
          if (downloadBase64Pdf(certificatePayload, row?.courseName)) {
            setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "success" }));
            setTimeout(() => {
              setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "idle" }));
            }, 2000);
            return;
          }
          downloadUrl = extractDownloadUrl(certificatePayload);
        } catch (error) {
          downloadUrl = null;
        }
      }

      downloadUrl = downloadUrl || withDownloadOwn(certificate?.url);
      if (downloadUrl) {
        window.location.href = downloadUrl;
        setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "success" }));
        setTimeout(() => {
          setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "idle" }));
        }, 2000);
        return;
      }

      if (certificate?.cmid && user?.username) {
        const { key } = await requestAutoLogin({
          username: user.username,
          baseUrl: config?.baseURL || baseURL,
          tipddytoken: user.token,
        });
        const url = `https://api-interfaz-alumnos.tipddy.cl/history/cert/${certificate.cmid}?userid=${user?.id || 0}&domain=${encodeURIComponent(config?.baseURL || baseURL)}&userkey=${key}`;
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "idle" }));
    } catch (error) {
      setDownloadStatusByCourseId((current) => ({ ...current, [courseId]: "idle" }));
    }
  }, [config?.baseURL, progressRows, user?.id, user?.token, user?.username]);

  const handleSort = useCallback((key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "courseName" ? "asc" : "desc" };
    });
  }, []);

  const fetchDetail = useCallback(async (courseId) => {
    setDetailLoading(true);
    setDetailData(null);
    try {
      const [gradesData] = await moodle.getProgressDetail(courseId);
      const rawItems = gradesData?.usergrades?.[0]?.gradeitems ?? [];
      const filteredItems = rawItems
        .filter((item) => item.itemtype !== "course" && item.itemtype !== "category")
        .filter((item) => item.itemmodule !== "folder")
        .filter((item) => item.itemname !== null && item.itemname !== "");
      setDetailData(filteredItems);
    } catch (error) {
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  }, [moodle]);

  const handleRowClick = useCallback((course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.displayname || course.fullname || "");
    fetchDetail(course.id);
  }, [fetchDetail]);

  const handleBack = useCallback(() => {
    setSelectedCourseId(null);
    setSelectedCourseName("");
    setDetailData(null);
  }, []);

  return (
    <>
      {lang && (
        <>
          <Navbar />
          {sessionKey && (
            <iframe
              src={`${baseURL}auth/userkey_tipddy/login.php?key=${sessionKey}&wantsurl=${baseURL}`}
              id="login-token"
              frameBorder="0"
              width="0"
              allow="autoplay ; microphone; camera"
              height="0"
              title="login-token"
            />
          )}

          <div
            className="flex flex-col items-center w-full px-4 py-48 min-h-dvh"
            style={{
              backgroundColor: theme.colors.backgroundColorBody ? theme.colors.backgroundColorBody : theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgb(34, 34, 34)",
              gap: "36px",
            }}
          >
            <div className="container w-full space-y-3">
              <style>{`
                .progress-panels-wrapper { position: relative; overflow: hidden; }
                .progress-panel { width: 100%; transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease; will-change: transform; }
                .progress-panel-list { transform: translateX(0); opacity: 1; }
                .progress-panel-list.slide-out { transform: translateX(-100%); opacity: 0; position: absolute; top: 0; left: 0; right: 0; pointer-events: none; }
                .progress-panel-detail { transform: translateX(100%); opacity: 0; position: absolute; top: 0; left: 0; right: 0; pointer-events: none; }
                .progress-panel-detail.slide-in { transform: translateX(0); opacity: 1; position: relative; pointer-events: auto; }
              `}</style>

              <div className="flex items-center gap-3">
                {selectedCourseId && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors duration-150"
                    style={{
                      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                      color: theme.colors.textColorDefault,
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                    {labels.back}
                  </button>
                )}
                <p
                  className="font-medium text-4xl"
                  style={{
                    color: readableColor(theme.colors.backgroundColorBody ? theme.colors.backgroundColorBody : theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgb(34, 34, 34)"),
                  }}
                >
                  {selectedCourseId ? <MultiLangText content={selectedCourseName} /> : labels.title}
                </p>
              </div>

              {!selectedCourseId && (
                <div className="flex flex-wrap gap-4 w-full pb-2">
                  <ProgressKpiCard
                    label={labels.totalCourses}
                    value={loadingRows ? <Skeleton variant="text" width={48} sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }} /> : kpis.totalCourses}
                    icon={<SchoolIcon />}
                    color="#F4E8FF"
                  />
                  <ProgressKpiCard
                    label={labels.completedCourses}
                    value={loadingRows ? <Skeleton variant="text" width={48} sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }} /> : kpis.completedCourses}
                    icon={<CheckCircleIcon />}
                    color="#DCFCE7"
                  />
                  <ProgressKpiCard
                    label={labels.inProgressCourses}
                    value={loadingRows ? <Skeleton variant="text" width={48} sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }} /> : kpis.inProgressCourses}
                    icon={<TrendingUpIcon />}
                    color="#D5F4F8"
                  />
                </div>
              )}

              <div className="progress-panels-wrapper">
                <div className={`progress-panel progress-panel-list${selectedCourseId ? " slide-out" : ""}`}>
                  <div className="relative">
                    {loadingRows && renderLoadingOverlay(labels.loading)}
                    <ProgressTable headers={tableTitles} sortConfig={sortConfig} onSort={handleSort}>
                      {loadingRows ? (
                        <>
                          {[...Array(6)].map((_, i) => (
                          <tr key={`skel-${i}`} style={{ backgroundColor: i % 2 === 1 ? (theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F5F5F5") : (theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF") }}>
                            {[...Array(tableTitles.length)].map((_, j) => (
                              <td key={j} className="p-[24px] border-none">
                                <Skeleton
                                  variant="rectangular"
                                  height={20}
                                  sx={{
                                    borderRadius: "4px",
                                    bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                    width: j === 0 ? "80%" : j === 1 ? "80px" : "60%",
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                          ))}
                        </>
                      ) : (
                        sortedRows.map((row, idx) => (
                        <ProgressRowItem
                          key={row.courseId || idx}
                          course={row.course}
                          grade={row.finalGrade}
                          finalGrade={row.finalGrade}
                          progress={row.progressPercent}
                          progressPercent={row.progressPercent}
                          certificate={row.certificate}
                          downloadStatus={downloadStatusByCourseId[row.courseId] || "idle"}
                          onDownloadCertificate={handleDownloadCertificate}
                          onRowClick={handleRowClick}
                          labels={labels}
                          includeEncuesta={includeEncuesta}
                          rowIndex={idx}
                        />
                      ))
                      )}
                    </ProgressTable>
                  </div>
                </div>

                <div className={`progress-panel progress-panel-detail${selectedCourseId ? " slide-in" : ""}`}>
                  {detailLoading ? (
                    <div className="relative flex flex-col gap-2 p-4">
                      {renderLoadingOverlay(labels.loadingDetail)}
                      {[...Array(5)].map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          height={48}
                          sx={{
                            borderRadius: "6px",
                            bgcolor: theme.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
                          }}
                        />
                      ))}
                    </div>
                  ) : detailData ? (
                    detailData.length > 0 ? (
                      <ProgressTable headers={detailTitles}>
                        {detailData.map((detail, idx) => (
                          <ProgressRowItemDetail
                            key={idx}
                            detail={detail}
                            index={idx}
                            includeFeedback={includeFeedback}
                          />
                        ))}
                      </ProgressTable>
                    ) : (
                      <div
                        className="flex flex-col items-center justify-center p-12 rounded-xl text-center shadow-sm"
                        style={{
                          backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                          border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                        }}
                      >
                        <p className="font-lato font-medium text-[15px]" style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#4B5563" }}>
                          {labels.noEvaluations}
                        </p>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Progress;
